import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRestApiContext } from "@/context/api";
import { queryKeys } from "@/queries/queryKeys";
import {
  AuthTokensSettingsDto,
  CompanyPluginDto,
  ConnectMicrosoftCompanyAppDto,
  DefaultProductsSettingsDto,
  TeamAssignmentSettingsDto,
  UpdateCompanyDomainsDto,
  UpdateDefaultRolesSettingsDto,
  UpdateEntraIdConfigDto,
  UpdateSamlConfigurationDto,
  UpdateScimConfigDto,
} from "@/types/auth";

export const useAuthTokensSettings = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["authTokensSettings", companyId],
    queryFn: () => api.getAuthTokensSettings(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateAuthTokensSettings = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AuthTokensSettingsDto) => api.updateAuthTokensSettings(companyId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authTokensSettings", companyId] });
    },
  });
};

export const useTeamAssignmentSettings = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["teamAssignmentSettings", companyId],
    queryFn: () => api.getTeamAssignmentSettings(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateTeamAssignmentSettings = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: TeamAssignmentSettingsDto) =>
      api.updateTeamAssignmentSettings(companyId, dto),
    onSuccess: (updated) => {
      // Seeded first, so the saved choice shows at once instead of the old one until the refetch
      queryClient.setQueryData(["teamAssignmentSettings", companyId], updated);
      queryClient.invalidateQueries({ queryKey: ["teamAssignmentSettings", companyId] });
    },
  });
};

export const useCompanyInfo = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["companyInfo", companyId],
    queryFn: () => api.getCompanyInfo(companyId),
    enabled: !!companyId,
  });
};

export const useDefaultProducts = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["defaultProducts", companyId],
    queryFn: () => api.getDefaultProducts(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateDefaultProducts = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: DefaultProductsSettingsDto) => api.updateDefaultProducts(companyId, dto),
    onSuccess: (data) => {
      // Seed the cache from the response instead of invalidating: the toggles compute the next
      // product list from the cached one, so a refetch window would let a second click build on
      // the pre-change list and undo the first change.
      queryClient.setQueryData(["defaultProducts", companyId], data);
    },
  });
};

export const useDefaultRolesSettings = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["defaultRolesSettings", companyId],
    queryFn: () => api.getDefaultRolesSettings(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateDefaultRolesSettings = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateDefaultRolesSettingsDto) =>
      api.updateDefaultRolesSettings(companyId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defaultRolesSettings", companyId] });
    },
  });
};

export const useCompanySamlConfiguration = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["companySamlConfiguration", companyId],
    queryFn: () => api.getSamlConfiguration(companyId),
    enabled: !!companyId,
  });
};

export const useSetDefaultSamlConfiguration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configId: number) => api.setDefaultSamlConfiguration(companyId, configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySamlConfiguration", companyId] });
      // The dashboard reads the active connection's health, also shown on the SSO card
      queryClient.invalidateQueries({ queryKey: [queryKeys.DASHBOARD, companyId] });
    },
  });
};

export const useUpdateSamlConfiguration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, update }: { configId: number; update: UpdateSamlConfigurationDto }) =>
      api.updateSamlConfiguration(companyId, configId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySamlConfiguration", companyId] });
    },
  });
};

export const useUpdateCompanyDomains = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: UpdateCompanyDomainsDto) => api.updateCompanyDomains(companyId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySamlConfiguration", companyId] });
    },
  });
};

export const useClearDefaultSamlConfiguration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.clearDefaultSamlConfiguration(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySamlConfiguration", companyId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.DASHBOARD, companyId] });
    },
  });
};

export const useEntraIdConfiguration = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["entraIdConfiguration", companyId],
    queryFn: () => api.getEntraIdConfiguration(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateEntraIdConfiguration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateEntraIdConfigDto) => api.updateEntraIdConfiguration(companyId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entraIdConfiguration", companyId] });
    },
  });
};

export const useMicrosoftIntegrationStatus = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["microsoftIntegration", companyId],
    queryFn: () => api.getMicrosoftIntegrationStatus(companyId),
    enabled: !!companyId,
  });
};

export const useDisconnectMicrosoftIntegration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.disconnectMicrosoftIntegration(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["microsoftIntegration", companyId] });
    },
  });
};

export const useDisconnectMicrosoftBot = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.disconnectMicrosoftBot(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["microsoftIntegration", companyId] });
    },
  });
};

export const useConnectMicrosoftCompanyApp = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ConnectMicrosoftCompanyAppDto) =>
      api.connectMicrosoftCompanyApp(companyId, body),
    onSuccess: (result) => {
      if (result.status === "CONNECTED") {
        queryClient.invalidateQueries({ queryKey: ["microsoftIntegration", companyId] });
      }
    },
  });
};

export const useScimConfiguration = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["scimConfiguration", companyId],
    queryFn: () => api.getScimConfiguration(companyId),
    enabled: !!companyId,
  });
};

export const useGenerateScimToken = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.generateScimToken(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scimConfiguration", companyId] });
    },
  });
};

export const useRevokeScimToken = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.revokeScimToken(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scimConfiguration", companyId] });
    },
  });
};

/** Every save sends all SCIM settings, so the cards that split them up wait for each other's */
export const scimUpdateMutationKey = (companyId: string) => ["updateScimConfiguration", companyId];

export const useUpdateScimConfiguration = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: scimUpdateMutationKey(companyId),
    mutationFn: (dto: UpdateScimConfigDto) => api.updateScimConfiguration(companyId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scimConfiguration", companyId] });
    },
  });
};

export const useCompanyApiKeys = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["companyApiKeys", companyId],
    queryFn: () => api.listCompanyApiKeys(companyId),
    enabled: !!companyId,
  });
};

export const useCreateCompanyApiKey = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.createCompanyApiKey(companyId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companyApiKeys", companyId] });
    },
  });
};

export const useRevokeCompanyApiKey = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prefix: string) => api.revokeCompanyApiKey(companyId, prefix),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companyApiKeys", companyId] });
    },
  });
};

export const useServiceAccountStatus = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["serviceAccountStatus", companyId],
    queryFn: () => api.getServiceAccountStatus(companyId),
    enabled: !!companyId,
  });
};

export const useCreateServiceAccount = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.createServiceAccount(companyId),
    onSuccess: (data) => {
      // Seed the status cache from the POST response instead of invalidating: the
      // create endpoint returns the authoritative status, so this flips the gate to
      // the key form synchronously and leaves no window where a stale "no service
      // account" state re-enables the create button for a second submit.
      queryClient.setQueryData(["serviceAccountStatus", companyId], data);
    },
  });
};

export const useCompanyPlugins = (companyId: string) => {
  const api = useRestApiContext();

  return useQuery({
    queryKey: ["companyPlugins", companyId],
    queryFn: () => api.getCompanyPlugins(companyId),
    enabled: !!companyId,
    // Each attempt can wait out SkillPanel's own timeouts; the card offers Retry instead
    retry: false,
  });
};

export const useEnableCompanyPlugin = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pluginId: string) => api.enableCompanyPlugin(companyId, pluginId),
    onSuccess: (updated) => {
      // Seed the cache from the response instead of invalidating: CompanyPluginService caches
      // per replica, so an invalidated refetch can land on a pod that hasn't seen this write yet
      // and serve the pre-change list for up to the cache's TTL. The response is authoritative.
      queryClient.setQueryData<CompanyPluginDto[]>(["companyPlugins", companyId], (old) =>
        old?.map((p) => (p.pluginId === updated.pluginId ? updated : p)),
      );
    },
  });
};

export const useDisableCompanyPlugin = (companyId: string) => {
  const api = useRestApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pluginId: string) => api.disableCompanyPlugin(companyId, pluginId),
    onSuccess: (_, pluginId) => {
      // Same reasoning as useEnableCompanyPlugin: seed the cache rather than invalidate, since a
      // refetch could hit a replica whose per-JVM cache has not been evicted yet.
      queryClient.setQueryData<CompanyPluginDto[]>(["companyPlugins", companyId], (old) =>
        old?.map((p) => (p.pluginId === pluginId ? { ...p, enabled: false } : p)),
      );
    },
  });
};
